/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from "react";
import { inject } from "mobx-react";
import { injectIntl } from "react-intl";
import {
  getTotalPages,
  getCurrMaxPageWidth,
  getShouldRecomputePageSizes,
  getUseReactVirtualized,
  isTwoColumnViewMode,
  getColumnMaxWidth,
  getBottomPanelHeight,
  getDocumentBottomSpace
} from "selectors/documentSelectors";
import {
  getPagesViewYPos,
  getPagesViewXPos,
  isPagedViewScrolling,
  getSafeYOffset,
  getSafeXOffset
} from "selectors/pagedViewState";
import { pagesInView } from "selectors/pagedViewSelectors";
import { PAGE_BOTTOM_MARGIN, AJS_CUSTOM_SCROLL_EVT } from "constants/constants";
import { getPageOverlays } from "selectors/overlayDropinSelectors";
import { PagesView } from "components/document/page/PagesView";
import { ClassicPDFPageContainer } from "containers/document/page/ClassicPDFPageContainer";
import { pageContentHandler } from "pageContentHandler";
import * as constants from "constants/constants";
import { overlayEvents } from "overlays/OverlayEvents";
import { getPageHeight, getPageWidth, getPage, getRowHeight } from "selectors/pageSelectors";
import { getViewPortHeight, getViewPortWidth, getLastPageExtraBottomSpace } from "selectors/documentViewSelectors";
import { getMaxVerticalScrollSize } from "selectors/pagedViewState";
import { isIE } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { getMargin } from "selectors/zoom";
import { isInitialPageLoaded, getOpenAction } from "selectors/pagedViewState";
import { getViewPortWidthWithoutPagesViewScrollbar } from "../../selectors/documentViewSelectors";
import { getPagesViewElementHandle } from "selectors/pagedViewState";
import { triggerPagesInViewEvent } from "publicApis/Events/events";
import { getPagesInView, getPagesInDOM } from "selectors/documentSelectors";
import { getViewMode, getIsInPlaceMode } from "selectors/viewModeSelectors";
import { isEqual } from "utils/lodashUtils";
import { shouldShowScrubber } from "selectors/pagedViewState";
import {
  getShouldUseModernViewer,
  getShouldUseMobileModernViewer,
  isRHPResizing,
  getHideStickyToolOnTap,
  getSwitchPreviewState
} from "selectors/appSelectors";
import { getTotalRows, getPageIndex } from "selectors/pageSelectors";
import { pagesViewLoader, SWITCH_PREVIEW_STATE } from "constants/enums";

const mapStoreToProps = (stores, ownProps) => {
  const pagesInViewObj = pagesInView(stores);
  const viewPortHeight = getViewPortHeight(stores);
  const viewPortWidth = getViewPortWidth(stores);
  const lastPageExtraBottomSpace = getLastPageExtraBottomSpace(stores);
  const isTwoColumn = isTwoColumnViewMode(stores);
  const isModernViewer = getShouldUseModernViewer(stores);
  return {
    pagesInViewStart: pagesInViewObj.start,
    pagesInViewEnd: pagesInViewObj.end,
    pageCount: getTotalPages(stores),
    PageContainer: ClassicPDFPageContainer,
    pagesViewYPos: getSafeYOffset(stores, viewPortHeight, getPagesViewYPos(stores)),
    pagesViewXPos: getSafeXOffset(stores, viewPortWidth, getPagesViewXPos(stores)),
    isScrolling: isPagedViewScrolling(stores),
    setFocusMethod: ownProps.setFocusMethod,
    topPadding: constants.PAGES_TOP_PADDING,
    isTwoColumn,
    onPagesViewMount: () => {
      const { pageIndex, x, y } = getOpenAction(stores);
      stores.pagedViewState.goToLocation(pageIndex, x, y);
      if (getSwitchPreviewState(stores) === SWITCH_PREVIEW_STATE.SWITCHED) {
        stores.appStore.setSwitchPreviewState(SWITCH_PREVIEW_STATE.POST_SWITCH);
      }
    },
    onPagesInViewChange: pagesInView => {
      stores.documentStore.loadPagesInView(pagesInView);
      triggerPagesInViewEvent(stores);
    },
    onScroll: event => {
      if (getIsInPlaceMode(stores)) {
        return;
      }
      stores.pagedViewState.onPagesViewScroll(event);
      let scrollLeft = event.target ? event.target.scrollLeft : event.scrollLeft;
      let scrollTop = event.target ? event.target.scrollTop : event.scrollTop;
      stores.documentStore.updateScrollDirection(scrollTop);
      let margins = getMargin(stores);
      let scrollWidth = getPagesViewElementHandle(stores)
        ? getPagesViewElementHandle(stores).scrollWidth
        : getCurrMaxPageWidth(stores) + margins.left + margins.right;
      let maxScrollLeft = scrollWidth - getViewPortWidthWithoutPagesViewScrollbar(stores);
      maxScrollLeft = Math.max(0, maxScrollLeft);
      if (scrollLeft >= 0 && scrollTop >= 0 && scrollLeft <= maxScrollLeft) {
        const scrollEvent = event.target ? event : { target: event };
        overlayEvents.trigger("pagesViewScrolled", scrollEvent);
      }
      if (getUseReactVirtualized(stores)) {
        let evt = null;
        if (!isIE()) {
          evt = new CustomEvent(AJS_CUSTOM_SCROLL_EVT, {});
        } else {
          evt = document.createEvent("CustomEvent");
          evt.initCustomEvent(AJS_CUSTOM_SCROLL_EVT, true, true, {});
        }
        window.dispatchEvent(evt);
      } else {
        stores.touchSelStore.hideContextMenu(true);
      }
    },
    onScrollEnd: evt => {
      stores.touchSelStore.hideContextMenu(false);
      overlayEvents.trigger("pagesViewScrollEnd", evt);
      triggerPagesInViewEvent(stores);
    },
    customScroll: () => {
      stores.touchSelStore.hideContextMenu(true);
    },
    customScrollEnd: evt => {
      stores.touchSelStore.hideContextMenu(false);
      overlayEvents.trigger("pagesViewScrollEnd", evt);
      triggerPagesInViewEvent(stores);
    },
    onMouseDown: evt => {
      pageContentHandler.handleMouseDownOnDocument(evt, stores);
    },
    onClick: evt => {
      overlayEvents.trigger("documentclick", evt);
    },
    pageOverlays: getPageOverlays(stores).peek(),
    getPageViewHeight: ({ index }) => {
      let retVal = 0;
      const lastRowIndex = getTotalRows(stores) - 1;
      retVal = getRowHeight(stores, index) + PAGE_BOTTOM_MARGIN;
      if (index === lastRowIndex) {
        retVal += lastPageExtraBottomSpace;
      }
      return retVal;
    },
    parentViewHeight: viewPortHeight,
    parentViewWidth: viewPortWidth,
    getMaxWidth: ({ index }) => getColumnMaxWidth(stores, index),
    shouldRecomputePageSizes: getShouldRecomputePageSizes(stores) === true,
    resetShouldRecomputePageSizes: () => {
      stores.documentStore.setShouldRecomputePageSizes(false);
    },
    useReactVirtualized: getUseReactVirtualized(stores),
    overscanRowCount: constants.DOCUMENT_OVERSCAN_ROW_COUNT,
    onSectionRendered: ({
      rowStartIndex,
      rowStopIndex,
      columnStartIndex,
      columnStopIndex,
      columnOverscanStartIndex,
      columnOverscanStopIndex,
      rowOverscanStartIndex,
      rowOverscanStopIndex
    }) => {
      const pagesInView = {
        start: getPageIndex(stores, rowStartIndex, columnStartIndex),
        end: getPageIndex(stores, rowStopIndex, columnStopIndex)
      };
      stores.documentStore.loadPagesInView(pagesInView);
      let oldPagesInView = getPagesInView(stores);

      if (
        oldPagesInView.start === undefined ||
        oldPagesInView.end === undefined ||
        !isEqual(oldPagesInView, pagesInView)
      ) {
        triggerPagesInViewEvent(stores);
        stores.documentStore.setPagesInView(pagesInView);
      }
      const pagesInDOM = {
        start: getPageIndex(stores, rowOverscanStartIndex, columnOverscanStartIndex),
        end: getPageIndex(stores, rowOverscanStopIndex, columnOverscanStopIndex)
      };
      const oldPagesInDOM = getPagesInDOM(stores);
      if (oldPagesInDOM.start === undefined || oldPagesInDOM.end === undefined || !isEqual(oldPagesInDOM, pagesInDOM)) {
        stores.documentStore.setPagesInDOM(pagesInDOM);
      }
    },
    estimatedRowSize: getPageHeight(stores, 0) + PAGE_BOTTOM_MARGIN,
    estimatedColumnSize: isTwoColumn ? getPageWidth(stores, 0) + constants.TWO_COLUMN_MARGIN : getPageWidth(stores, 0),
    verticalMaxScrollSize: getMaxVerticalScrollSize(stores),
    setElementHandle: handle => {
      stores.pagedViewState.setPagesViewElementHandle(handle);
    },
    setNonReactVPagesViewHandle: handle => {
      stores.pagedViewState.setNonReactVPagesViewHandle(handle);
    },
    shouldShowScrubber: shouldShowScrubber(stores),
    shouldHighlightGridOnFocus:
      getShouldUseModernViewer(stores) && !getShouldUseMobileModernViewer(stores) && !getIsInPlaceMode(stores),
    shouldUseMobileModernViewer: getShouldUseMobileModernViewer(stores),
    currPageIndex: getCurrentPageIndex(stores),
    margins: getMargin(stores),
    viewMode: getViewMode(stores),
    totalRows: getTotalRows(stores),
    getPageIndex: (rowIndex, columnIndex) => getPageIndex(stores, rowIndex, columnIndex),
    columnCount: isTwoColumn === true ? 2 : 1,
    isModernViewerEnabled: isModernViewer,
    bottomPanelHeight: getBottomPanelHeight(stores),
    isRHPResizing: isRHPResizing(stores),
    pagesViewLoader: pagesViewLoader.CLASSIC_VIEW_CONTAINER,
    hideStickyToolOnTap: getHideStickyToolOnTap(stores),
    switchPreviewState: getSwitchPreviewState(stores),
    documentBottomSpace: getDocumentBottomSpace(stores)
  };
};

const ClassicPDFView = injectIntl(inject(mapStoreToProps)(PagesView));

export { ClassicPDFView };
